import React from 'react';
import { Box, Typography, Avatar, alpha, useTheme, Grid, Divider } from '@mui/material';
import NatureIcon from '@mui/icons-material/Nature';
import SavingsIcon from '@mui/icons-material/Savings';
import VerifiedIcon from '@mui/icons-material/Verified';
import GroupIcon from '@mui/icons-material/Group';
import { StyledCard, StyledCardHeader, StyledCardContent, MetricChip, formatDisplayItem } from '@features/analysis/components/StyledComponents';
import { AIAnalysisResult } from '@features/analysis/types';

interface SustainabilityCardProps {
  analysis: AIAnalysisResult;
}

const SustainabilityCard: React.FC<SustainabilityCardProps> = ({ analysis }) => {
  const theme = useTheme();
  const sustainabilityAnalysis = analysis.sustainabilityAnalysis;

  // Helper function to get initiative category icon
  const getInitiativeIcon = (initiative: string) => {
    const init = initiative.toLowerCase();
    if (init.includes('energy') || init.includes('solar') || init.includes('lighting')) {
      return '⚡';
    } else if (init.includes('water') || init.includes('conservation')) {
      return '💧';
    } else if (init.includes('waste') || init.includes('recycling')) {
      return '♻️';
    } else if (init.includes('certification') || init.includes('leed') || init.includes('green')) {
      return '🏆';
    } else {
      return '🌿';
    }
  };

  return (
    <StyledCard>
      <StyledCardHeader
        title="Sustainability Analysis"
        subheader="ESG factors and environmental initiatives"
        avatar={
          <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main }}>
            <NatureIcon />
          </Avatar>
        }
      />
      <StyledCardContent>
        {/* Current Performance */}
        {sustainabilityAnalysis?.currentPerformance && (
          <Box sx={{ mb: 3, p: 2.5, bgcolor: alpha(theme.palette.primary.main, 0.03), borderRadius: 2, border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}` }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: theme.palette.primary.main, mb: 1.5 }}>
              🌍 Current Sustainability Performance
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem', lineHeight: 1.5 }}>
              {sustainabilityAnalysis.currentPerformance}
            </Typography>
          </Box>
        )}

        {/* Key Benefits Overview */}
        {(sustainabilityAnalysis?.costSavingsPotential || sustainabilityAnalysis?.guestImpact) && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Cost Savings */}
            {sustainabilityAnalysis?.costSavingsPotential && (
              <Grid item xs={12} md={6}>
                <Box sx={{
                  p: 2.5,
                  bgcolor: alpha(theme.palette.success.main, 0.03),
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
                  textAlign: 'center'
                }}>
                  <SavingsIcon sx={{ color: theme.palette.success.main, fontSize: 32, mb: 1 }} />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Cost Savings Potential
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.success.main, fontSize: '0.9rem' }}>
                    {sustainabilityAnalysis.costSavingsPotential}
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* Guest Impact */}
            {sustainabilityAnalysis?.guestImpact && (
              <Grid item xs={12} md={6}>
                <Box sx={{
                  p: 2.5,
                  bgcolor: alpha(theme.palette.info.main, 0.03),
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
                  textAlign: 'center'
                }}>
                  <GroupIcon sx={{ color: theme.palette.info.main, fontSize: 32, mb: 1 }} />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Guest Impact
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.info.main, fontSize: '0.9rem' }}>
                    {sustainabilityAnalysis.guestImpact}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        )}

        {/* Recommended Initiatives */}
        {sustainabilityAnalysis?.recommendedInitiatives && sustainabilityAnalysis.recommendedInitiatives.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 2 }}>
              🌱 Recommended Sustainability Initiatives
            </Typography>
            <Grid container spacing={1.5}>
              {sustainabilityAnalysis.recommendedInitiatives.map((initiative, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Box sx={{
                    p: 2,
                    bgcolor: alpha(theme.palette.success.main, 0.03),
                    borderRadius: 1.5,
                    border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <Typography variant="h6" sx={{ fontSize: '1.2rem' }}>
                      {getInitiativeIcon(initiative)}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem', flex: 1 }}>
                      {formatDisplayItem(initiative)}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Certification Opportunities */}
        {sustainabilityAnalysis?.certificationOpportunities && sustainabilityAnalysis.certificationOpportunities.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 2 }}>
              <VerifiedIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'text-bottom' }} />
              Certification Opportunities
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {sustainabilityAnalysis.certificationOpportunities.map((certification, index) => (
                <MetricChip
                  key={index}
                  label={formatDisplayItem(certification)}
                  color="success"
                  variant="outlined"
                  size="small"
                  sx={{
                    bgcolor: alpha(theme.palette.success.main, 0.03),
                    borderColor: theme.palette.success.main,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.success.main, 0.08),
                    }
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* ESG Impact Summary */}
        {(sustainabilityAnalysis?.costSavingsPotential || sustainabilityAnalysis?.guestImpact || (sustainabilityAnalysis?.recommendedInitiatives && sustainabilityAnalysis.recommendedInitiatives.length > 0)) && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ p: 2, bgcolor: alpha(theme.palette.success.main, 0.03), borderRadius: 1.5 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                🎯 ESG Impact Overview
              </Typography>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Environmental</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
                      {sustainabilityAnalysis?.recommendedInitiatives?.filter(init =>
                        init.toLowerCase().includes('energy') ||
                        init.toLowerCase().includes('water') ||
                        init.toLowerCase().includes('waste')
                      ).length || 0} Initiatives
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Social</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.info.main }}>
                      {sustainabilityAnalysis?.guestImpact ? 'Positive' : 'To Assess'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Governance</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.warning.main }}>
                      {sustainabilityAnalysis?.certificationOpportunities?.length || 0} Opportunities
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </>
        )}

        {/* Empty State */}
        {!sustainabilityAnalysis && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <NatureIcon sx={{ fontSize: 48, color: theme.palette.text.disabled, mb: 2 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              No sustainability analysis available.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
              Include 'Sustainability' in your focus areas for ESG insights and green initiative recommendations.
            </Typography>
          </Box>
        )}
      </StyledCardContent>
    </StyledCard>
  );
};

export default SustainabilityCard;